/**
 * Product usage analytics transport (authenticated, Postgres-backed).
 *
 * Sibling of the perf beacon path — JWT required, so we use fetch+keepalive
 * (not sendBeacon). Identity is stamped on the proxy; this client never sends
 * user_id / organization_id.
 */
import { createOntologyHeaders, PROXY_API_V1_URL } from '../api/core'
import { getUsageEventsUrl, isUsageAnalyticsEnabled } from '../config'
import { featureFromRouteLabel, type UsageFeature } from './feature-map'
import { routeLabelFromPath } from './route-label'

export type UsageEventType = 'page_view' | 'heartbeat' | 'action'

export type UsageAction =
  | 'message_sent'
  | 'dashboard_saved'
  | 'config_saved'
  | 'insight_corrected'
  | 'ingest_started'
  | 'ingest_uploaded'
  | 'ingest_processed'
  | 'ingest_question_answered'
  | 'branch_created'
  | 'branch_committed'
  | 'branch_merged'

export interface UsageEvent {
  session_id: string
  event_type: UsageEventType
  feature: UsageFeature
  route?: string
  action?: UsageAction
  occurred_at: string
}

const FLUSH_DEBOUNCE_MS = 2_000
const MAX_BUFFERED_EVENTS = 100
const HEARTBEAT_MS = 60_000
const SESSION_IDLE_MS = 30 * 60_000
const SESSION_STORAGE_KEY = 'axilon.usage.session_id'
const SESSION_LAST_KEY = 'axilon.usage.session_last'

let buffer: UsageEvent[] = []
let flushTimer: ReturnType<typeof setTimeout> | null = null
let heartbeatTimer: ReturnType<typeof setInterval> | null = null
let unloadListenersInstalled = false
let heartbeatListenersInstalled = false
let currentRouteLabel = '/'
let currentFeature: UsageFeature = 'other'

function canSend(): boolean {
  return (
    typeof window !== 'undefined' &&
    isUsageAnalyticsEnabled() &&
    (getUsageEventsUrl().length > 0 || PROXY_API_V1_URL.length > 0)
  )
}

function nowIso(): string {
  return new Date().toISOString()
}

function readSessionStorage(key: string): string | null {
  try {
    return sessionStorage.getItem(key)
  } catch {
    return null
  }
}

function writeSessionStorage(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value)
  } catch {
    /* private mode / quota — fall through with in-memory session */
  }
}

let memorySessionId: string | null = null

function newSessionId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `s-${Date.now()}-${Math.random().toString(36).slice(2)}`
}

/** Session id: UUID in sessionStorage, reset after 30 min idle. */
export function getUsageSessionId(): string {
  const lastRaw = readSessionStorage(SESSION_LAST_KEY)
  const last = lastRaw ? Number(lastRaw) : 0
  const idle = Date.now() - last > SESSION_IDLE_MS
  let id = readSessionStorage(SESSION_STORAGE_KEY) ?? memorySessionId
  if (!id || idle) {
    id = newSessionId()
    memorySessionId = id
    writeSessionStorage(SESSION_STORAGE_KEY, id)
  }
  writeSessionStorage(SESSION_LAST_KEY, String(Date.now()))
  memorySessionId = id
  return id
}

function enqueue(event: UsageEvent): void {
  if (!canSend()) return
  installUnloadFlush()
  ensureHeartbeat()
  buffer.push(event)
  if (buffer.length > MAX_BUFFERED_EVENTS) {
    buffer.splice(0, buffer.length - MAX_BUFFERED_EVENTS)
  }
  if (!flushTimer) {
    flushTimer = setTimeout(flushUsageEvents, FLUSH_DEBOUNCE_MS)
  }
}

async function send(events: UsageEvent[]): Promise<void> {
  const url = getUsageEventsUrl()
  if (!url || typeof fetch !== 'function') return
  const headers = createOntologyHeaders()
  headers.set('Content-Type', 'application/json')
  try {
    await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ events }),
      keepalive: true
    })
  } catch {
    /* best-effort */
  }
}

/** Flush the buffer immediately. */
export function flushUsageEvents(): void {
  if (flushTimer) {
    clearTimeout(flushTimer)
    flushTimer = null
  }
  if (buffer.length === 0) return
  const events = buffer
  buffer = []
  if (!canSend()) return
  void send(events)
}

function installUnloadFlush(): void {
  if (unloadListenersInstalled || typeof window === 'undefined') return
  unloadListenersInstalled = true
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushUsageEvents()
  })
  window.addEventListener('pagehide', () => flushUsageEvents())
}

function isFocusedAndVisible(): boolean {
  if (typeof document === 'undefined') return false
  return document.visibilityState === 'visible' && document.hasFocus()
}

function tickHeartbeat(): void {
  if (!canSend() || !isFocusedAndVisible()) return
  enqueue({
    session_id: getUsageSessionId(),
    event_type: 'heartbeat',
    feature: currentFeature,
    route: currentRouteLabel,
    occurred_at: nowIso()
  })
}

function ensureHeartbeat(): void {
  if (heartbeatListenersInstalled || typeof window === 'undefined') return
  if (!canSend()) return
  heartbeatListenersInstalled = true
  heartbeatTimer = setInterval(tickHeartbeat, HEARTBEAT_MS)
  window.addEventListener('focus', () => {
    /* next interval tick will fire when focused */
  })
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushUsageEvents()
  })
}

/** Record a SPA page view for the given location (pathname+search). */
export function trackPageView(pathWithSearch: string): void {
  if (!canSend()) return
  const route = routeLabelFromPath(pathWithSearch)
  const feature = featureFromRouteLabel(route)
  currentRouteLabel = route
  currentFeature = feature
  enqueue({
    session_id: getUsageSessionId(),
    event_type: 'page_view',
    feature,
    route,
    occurred_at: nowIso()
  })
}

/** Record an explicit product action (allowlisted on the proxy). */
export function trackUsageAction(feature: UsageFeature, action: UsageAction): void {
  if (!canSend()) return
  enqueue({
    session_id: getUsageSessionId(),
    event_type: 'action',
    feature,
    action,
    route: currentRouteLabel,
    occurred_at: nowIso()
  })
}

/** Start heartbeat + unload flush (idempotent). Call once from AppLayout. */
export function initUsageAnalytics(): void {
  if (!canSend()) return
  installUnloadFlush()
  ensureHeartbeat()
}

/** Test seam. */
export function __resetUsageTrackerForTests(): void {
  if (flushTimer) {
    clearTimeout(flushTimer)
    flushTimer = null
  }
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer)
    heartbeatTimer = null
  }
  buffer = []
  unloadListenersInstalled = false
  heartbeatListenersInstalled = false
  memorySessionId = null
  currentRouteLabel = '/'
  currentFeature = 'other'
}
